import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { initialBooks, completeSeriesData } from './constants'
import { getSortedBooks } from './utils'
export function useBookshelfState() {
  const navigate = useNavigate()

  // Books state with editing
  const [books, setBooks] = useState(initialBooks)
  const [activeTab, setActiveTab] = useState('ownedShelf')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedGenre, setSelectedGenre] = useState('')
  const [selectedSubgenre, setSelectedSubgenre] = useState('')
  const [selectedRelationship, setSelectedRelationship] = useState('')
  const [selectedFormat, setSelectedFormat] = useState('')
  const [viewMode, setViewMode] = useState<'spine' | 'cover'>('spine')
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const dragOverIdRef = useRef<string | null>(null)
  const [darkMode, setDarkMode] = useState(false)
  const [sortOption, setSortOption] = useState('seriesOrder')
  const [seriesFilter, setSeriesFilter] = useState('allBooks')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAuthor, setSelectedAuthor] = useState('all')
  const [hoveredBookId, setHoveredBookId] = useState<string | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedBooks, setSelectedBooks] = useState<string[]>([])
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0,
  })

  // Check system preference for dark mode + track mouse position
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true'
    setDarkMode(savedDarkMode)
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY,
      })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  // Get unique authors from books
  const authors = useMemo(() => {
    const uniqueAuthors = [...new Set(books.map((book) => book.author))].sort()
    return ['all', ...uniqueAuthors]
  }, [books])

  // Get unique genres, subgenres, and relationship types
  const genres = useMemo(() => {
    return [...new Set(books.map((book) => book.genre))].sort()
  }, [books])
  const subgenres = useMemo(() => {
    return [
      ...new Set(books.map((book) => book.subgenre).filter(Boolean)),
    ].sort()
  }, [books])
  const relationshipTypes = useMemo(() => {
    return [
      ...new Set(books.map((book) => book.relationshipType).filter(Boolean)),
    ].sort()
  }, [books])
  const formats = useMemo(() => {
    return [...new Set(books.map((book) => book.format))].sort()
  }, [books])

  // Filter books by active tab + search + genre + author
  const filteredBooks = useMemo(() => {
    return books.filter((book) => {
      if (activeTab !== 'ownedShelf' && book.status !== activeTab) return false
      const matchesSearch =
        book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.series &&
          book.series.toLowerCase().includes(searchTerm.toLowerCase())) ||
        book.author.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesGenre = selectedGenre === '' || book.genre === selectedGenre
      const matchesSubgenre =
        selectedSubgenre === '' || book.subgenre === selectedSubgenre
      const matchesRelationship =
        selectedRelationship === '' ||
        book.relationshipType === selectedRelationship
      const matchesFormat =
        selectedFormat === '' || book.format === selectedFormat
      const matchesAuthor =
        selectedAuthor === 'all' || book.author === selectedAuthor
      return (
        matchesSearch &&
        matchesGenre &&
        matchesSubgenre &&
        matchesRelationship &&
        matchesFormat &&
        matchesAuthor
      )
    })
  }, [
    books,
    activeTab,
    searchTerm,
    selectedGenre,
    selectedSubgenre,
    selectedRelationship,
    selectedFormat,
    selectedAuthor,
  ])

  // Sorted filtered books with series grouping
  const sortedBooks = useMemo(() => {
    if (activeTab === 'ownedShelf') {
      return filteredBooks
    }
    return getSortedBooks(filteredBooks)
  }, [filteredBooks, activeTab])

  // Get series data for the Owned Shelf tab
  const seriesData = useMemo(() => {
    if (activeTab !== 'ownedShelf') return []
    const seriesEntries = Object.entries(completeSeriesData).filter(
      ([_seriesName, seriesBooks]) => {
        if (seriesFilter === 'all' || seriesFilter === 'allBooks') return true
        if (seriesFilter === 'completed') {
          return seriesBooks.every((book) => book.owned)
        }
        if (seriesFilter === 'ongoing') {
          return (
            seriesBooks.some((book) => book.owned) &&
            seriesBooks.some((book) => !book.owned)
          )
        }
        if (seriesFilter === 'standalone') {
          return false
        }
        return true
      },
    )
    const authorFilteredEntries =
      selectedAuthor === 'all'
        ? seriesEntries
        : seriesEntries.filter(([_seriesName, seriesBooks]) => {
            const ownedBook = seriesBooks.find((book) => book.owned)
            if (!ownedBook) return false
            const fullBook = initialBooks.find((b) => b.id === ownedBook.id)
            return fullBook?.author === selectedAuthor
          })
    return authorFilteredEntries.sort((a, b) => {
      const [seriesNameA, booksA] = a
      const [seriesNameB, booksB] = b
      if (sortOption === 'alphabetical') {
        return seriesNameA.localeCompare(seriesNameB)
      }
      if (sortOption === 'dateOwned') {
        const earliestOwnedA = Math.min(
          ...booksA
            .filter((book) => book.owned)
            .map((book) => {
              const fullBook = books.find((b) => b.id === book.id)
              return fullBook
                ? new Date(fullBook.dateOwned).getTime()
                : Infinity
            }),
        )
        const earliestOwnedB = Math.min(
          ...booksB
            .filter((book) => book.owned)
            .map((book) => {
              const fullBook = books.find((b) => b.id === book.id)
              return fullBook
                ? new Date(fullBook.dateOwned).getTime()
                : Infinity
            }),
        )
        return earliestOwnedB - earliestOwnedA
      }
      return seriesNameA.localeCompare(seriesNameB)
    })
  }, [activeTab, books, seriesFilter, sortOption, selectedAuthor])

  // Get standalone books for the Owned Shelf tab
  const standaloneBooks = useMemo(() => {
    if (activeTab !== 'ownedShelf') return []
    if (seriesFilter !== 'standalone' && seriesFilter !== 'allBooks') return []
    const filtered = filteredBooks.filter(
      (book) => !book.series || book.series === '',
    )
    return selectedAuthor === 'all'
      ? filtered
      : filtered.filter((book) => book.author === selectedAuthor)
  }, [activeTab, filteredBooks, seriesFilter, selectedAuthor])

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    setSelectedBooks([])
    setIsEditMode(false)
  }

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditMode) {
      setSelectedBooks([])
    }
    setIsEditMode(!isEditMode)
  }

  // Handle selecting/deselecting a book in edit mode
  const toggleBookSelection = (id: string) => {
    if (selectedBooks.includes(id)) {
      setSelectedBooks(selectedBooks.filter((bookId) => bookId !== id))
    } else {
      setSelectedBooks([...selectedBooks, id])
    }
  }

  // Handle selecting all visible books
  const selectAllBooks = () => {
    if (activeTab === 'ownedShelf') {
      if (seriesFilter === 'allBooks' || seriesFilter === 'standalone') {
        setSelectedBooks(filteredBooks.map((book) => book.id))
      } else {
        const seriesBookIds = seriesData.flatMap(([_, seriesBooks]) =>
          seriesBooks.filter((book) => book.owned).map((book) => book.id),
        )
        setSelectedBooks(seriesBookIds)
      }
    } else {
      setSelectedBooks(sortedBooks.map((book) => book.id))
    }
  }

  // Clear all selections
  const clearSelection = () => {
    setSelectedBooks([])
  }

  // Handle bulk actions for selected books
  const bulkChangeStatus = (newStatus: string) => {
    setBooks((prev) =>
      prev.map((book) =>
        selectedBooks.includes(book.id)
          ? {
              ...book,
              status: newStatus,
            }
          : book,
      ),
    )
    setSelectedBooks([])
  }

  // Handle deleting a book
  function handleDelete(id: string) {
    if (confirm('Are you sure you want to delete this book from your shelf?')) {
      setBooks((prev) => prev.filter((b) => b.id !== id))
      if (selectedBooks.includes(id)) {
        setSelectedBooks(selectedBooks.filter((bookId) => bookId !== id))
      }
    }
  }

  // Bulk delete selected books
  const bulkDelete = () => {
    if (
      confirm(
        `Are you sure you want to delete ${selectedBooks.length} book(s) from your shelf?`,
      )
    ) {
      setBooks((prev) =>
        prev.filter((book) => !selectedBooks.includes(book.id)),
      )
      setSelectedBooks([])
    }
  }

  // Change book status (move to different tab)
  function moveBookStatus(id: string, newStatus: string) {
    setBooks((prev) =>
      prev.map((b) =>
        b.id === id
          ? {
              ...b,
              status: newStatus,
            }
          : b,
      ),
    )
  }

  // Navigate to book detail page
  function navigateToBookDetail(id: string) {
    navigate(`/book/${id}`)
  }

  // Drag & Drop handlers for manual reordering
  function handleDragStart(id: string) {
    setDraggingId(id)
  }
  function handleDragEnter(id: string) {
    dragOverIdRef.current = id
  }
  function handleDragEnd() {
    if (
      !draggingId ||
      !dragOverIdRef.current ||
      draggingId === dragOverIdRef.current
    ) {
      setDraggingId(null)
      dragOverIdRef.current = null
      return
    }
    setBooks((prevBooks) => {
      const currentTabBooks = prevBooks.filter((b) => b.status === activeTab)
      const otherBooks = prevBooks.filter((b) => b.status !== activeTab)
      const dragIndex = currentTabBooks.findIndex((b) => b.id === draggingId)
      const overIndex = currentTabBooks.findIndex(
        (b) => b.id === dragOverIdRef.current,
      )
      if (dragIndex === -1 || overIndex === -1) return prevBooks
      const reordered = [...currentTabBooks]
      const [dragged] = reordered.splice(dragIndex, 1)
      reordered.splice(overIndex, 0, dragged)
      return [...otherBooks, ...reordered]
    })
    setDraggingId(null)
    dragOverIdRef.current = null
  }
  return {
    // State values
    books,
    activeTab,
    searchTerm,
    selectedGenre,
    selectedSubgenre,
    selectedRelationship,
    selectedFormat,
    viewMode,
    darkMode,
    sortOption,
    seriesFilter,
    showFilters,
    selectedAuthor,
    hoveredBookId,
    isEditMode,
    selectedBooks,
    mousePosition,
    // Setters
    setSearchTerm,
    setSelectedGenre,
    setSelectedSubgenre,
    setSelectedRelationship,
    setSelectedFormat,
    setViewMode,
    setSortOption,
    setSeriesFilter,
    setShowFilters,
    setSelectedAuthor,
    setHoveredBookId,
    // Computed values
    authors,
    genres,
    subgenres,
    relationshipTypes,
    formats,
    filteredBooks,
    sortedBooks,
    seriesData,
    standaloneBooks,
    // Handlers
    handleTabChange,
    toggleEditMode,
    toggleBookSelection,
    selectAllBooks,
    clearSelection,
    bulkChangeStatus,
    handleDelete,
    bulkDelete,
    moveBookStatus,
    navigateToBookDetail,
    handleDragStart,
    handleDragEnter,
    handleDragEnd,
  }
}
